import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { DateTime } from 'luxon';
import { setAvailability } from '../../store/availability/actions';
import { fetchTrainingSession, fetchTrainingAvailability } from '../../store/calendar/actions';
import {
  getTrainingAvailability,
  getTrainingSession,
  getTrainingAvailabilityLoadingError,
  getTrainingSessionLoadingError,
  hasTrainingAvailabilityLoaded,
  hasTrainingSessionLoaded,
  isTrainingSessionLoading,
  isTrainingAvailabilityLoading,
} from '../../store/calendar/selectors';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import {
  TRAINING_AVAILABILITY_PROP_TYPES,
  TRAINING_SESSION_PROP_TYPES,
} from '../../lib/prop-types/training';
import TrainingSessionPage from '../../components/training-session';
import PageDataLoadError from '../../components/errors/page-data-load-error';
import { isLoggedIn as isLoggedInSelector } from '../../store/auth/selectors';

class Training extends React.Component {
  static async getInitialProps({ club, isServer, query, reduxStore, req, res }) {
    const sessionId = parseInt(query.id, 10);
    const initialProps = {
      activeSection: 'calendar',
      namespacesRequired: ['common'],
      pageType: 'calendar.training',
      sessionId,
    };

    if (!isServer) {
      return initialProps;
    }

    try {
      const { error } = await reduxStore.dispatch(fetchTrainingSession(club.id, sessionId, req));
      if (error && error.errorStatusCode && res) {
        res.statusCode = error.errorStatusCode;
      }

      return initialProps;
    } catch (err) {
      return initialProps;
    }
  }

  componentDidMount() {
    this.loadPageDataIfRequired();
    this.loadAvailabilityIfRequired();
  }

  componentDidUpdate() {
    this.loadAvailabilityIfRequired();
  }

  loadAvailabilityIfRequired = () => {
    const { isLoggedIn, isLoadingAvailability, hasLoadedAvailability, availabilityLoadingError } =
      this.props;
    if (
      isLoggedIn &&
      !hasLoadedAvailability &&
      !isLoadingAvailability &&
      !availabilityLoadingError
    ) {
      const { loadAvailability, sessionId } = this.props;
      loadAvailability(sessionId);
    }
  };

  loadPageDataIfRequired = () => {
    const { hasLoaded, isLoading, loadSession, loadingError } = this.props;
    if (!hasLoaded && !isLoading && !loadingError) {
      const {
        club: { id: clubId },
        sessionId,
      } = this.props;
      loadSession(clubId, sessionId);
    }
  };

  onUpdateAvailabilityHandler = (memberId, status, note) => {
    const {
      updateAvailability,
      session: { start } = {},
      club: { id: clubId },
    } = this.props;
    if (!start) return;

    updateAvailability(
      clubId,
      memberId,
      DateTime.fromISO(start).toFormat('yyyy-MM-dd'),
      status,
      note,
    );
  };

  render() {
    const { loadingError, club } = this.props;

    if (loadingError) {
      return <PageDataLoadError clubId={club.id} {...loadingError} />;
    }

    const { availabilities, isLoading, isLoadingAvailability, session } = this.props;
    return (
      <TrainingSessionPage
        club={club}
        availabilities={availabilities}
        isLoading={isLoading}
        isLoadingAvailability={isLoadingAvailability}
        onUpdateAvailability={this.onUpdateAvailabilityHandler}
        session={session}
      />
    );
  }
}

Training.defaultProps = {
  availabilityLoadingError: null,
  loadingError: null,
  session: null,
};

Training.propTypes = {
  availabilities: PropTypes.arrayOf(TRAINING_AVAILABILITY_PROP_TYPES).isRequired,
  availabilityLoadingError: PropTypes.shape(),
  club: CLUB_PROP_TYPES.isRequired,
  hasLoaded: PropTypes.bool.isRequired,
  hasLoadedAvailability: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isLoadingAvailability: PropTypes.bool.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  loadingError: PropTypes.shape(),
  loadAvailability: PropTypes.func.isRequired,
  loadSession: PropTypes.func.isRequired,
  sessionId: PropTypes.number.isRequired,
  session: TRAINING_SESSION_PROP_TYPES,
  updateAvailability: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) => ({
  availabilities: getTrainingAvailability(state, ownProps.sessionId),
  availabilityLoadingError: getTrainingAvailabilityLoadingError(state, ownProps.sessionId),
  hasLoaded: hasTrainingSessionLoaded(state, ownProps.sessionId),
  hasLoadedAvailability: hasTrainingAvailabilityLoaded(state, ownProps.sessionId),
  isLoading: isTrainingSessionLoading(state, ownProps.sessionId),
  isLoadingAvailability: isTrainingAvailabilityLoading(state, ownProps.sessionId),
  isLoggedIn: isLoggedInSelector(state),
  loadingError: getTrainingSessionLoadingError(state, ownProps.sessionId),
  session: getTrainingSession(state, ownProps.sessionId),
});

const dispatchToProps = {
  loadAvailability: fetchTrainingAvailability,
  loadSession: fetchTrainingSession,
  updateAvailability: setAvailability,
};

export default connect(mapStateToProps, dispatchToProps)(Training);
